import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { IoMdClose } from "react-icons/io";
import { createPortal } from "react-dom";
import { mainItemsHref } from "../../store/localItems";
import { filterAdminItemsByAccess } from "../../utils/adminAccess";
import ThemeSwitcher from "../ThemeSwitcher/ThemeSwitcher";
import { useSelector } from "react-redux";
import { formatNotificationCount, getNotificationCountForPath } from "../../utils/adminNotificationUtils";

const MonitorIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path d="M3 12h4l2-6 4 12 2-6h6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const RenderBurgerMenu = ({ user, show, onRequestClose, onRequestLogout }) => {
  const counts = useSelector((state) => state.notification.unreadByType);
  const [entered, setEntered] = useState(false);
  const panelRef = useRef(null);
  const rafRef = useRef(0);
  const drag = useRef({ active: false, sx: 0, sy: 0, dx: 0 });

  const visibleItems = useMemo(() => {
    return filterAdminItemsByAccess(mainItemsHref, user);
  }, [user]);

  const setDragX = (x) => {
    cancelAnimationFrame(rafRef.current);

    rafRef.current = requestAnimationFrame(() => {
      if (panelRef.current) {
        panelRef.current.style.setProperty("--dragX", `${x}px`);
      }
    });
  };

  useEffect(() => {
    if (!show) return undefined;

    const r = requestAnimationFrame(() => setEntered(true));

    const onKey = (e) => {
      if (e.key === "Escape") onRequestClose();
    };

    document.addEventListener("keydown", onKey);

    return () => {
      cancelAnimationFrame(r);
      document.removeEventListener("keydown", onKey);
      setEntered(false);
      setDragX(0);
    };
  }, [show, onRequestClose]);

  if (!show) return null;

  const onTouchStart = (e) => {
    const t = e.touches?.[0];

    if (!t) return;

    drag.current = { active: true, sx: t.clientX, sy: t.clientY, dx: 0 };
  };

  const onTouchMove = (e) => {
    if (!drag.current.active) return;

    const t = e.touches?.[0];

    if (!t) return;

    const dx = Math.min(0, t.clientX - drag.current.sx);
    const dy = Math.abs(t.clientY - drag.current.sy);

    if (Math.abs(dx) > dy && dx < 0) {
      e.preventDefault();
      drag.current.dx = dx;
      setDragX(dx);
    }
  };

  const onTouchEnd = () => {
    if (!drag.current.active) return;

    const shouldClose = drag.current.dx < -80;

    drag.current.active = false;
    drag.current.dx = 0;

    setDragX(0);

    if (shouldClose) onRequestClose();
  };

  return createPortal(
    <div
      className="burgerMenu__overlay"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="burgerMenu__backdrop" onClick={onRequestClose} />

      <div
        ref={panelRef}
        className={`burgerMenu__dropdown${entered ? " burgerMenu__dropdown--in" : ""}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="burgerMenu__header">
          <button className="burgerMenu__close" onClick={onRequestClose} aria-label="Закрыть">
            <IoMdClose size={22} />
          </button>

          <div className="burgerMenu__user">
            <NavLink to="/cabinet" onClick={onRequestClose}>
              <div className="burgerMenu__user-content">
                <div className="burgerMenu__user-avatar">
                  {String(user?.firstName || "U").charAt(0)}
                </div>

                <div className="burgerMenu__user-meta">
                  <div className="burgerMenu__user-name">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <span className="burgerMenu__user-mail">{user?.email}</span>
                </div>
              </div>
            </NavLink>
          </div>
        </div>

        <div className="burgerMenu__theme">
          <ThemeSwitcher userThemeType={user?.siteThemeType} />
        </div>

        <div className="burgerMenu__list">
          {user?.role === 'superAdmin' ? (
            <NavLink
              className="burgerMenu__link"
              to="/admin/system-monitor"
              onClick={onRequestClose}
            >
              <div className="burgerMenu__link-icon"><MonitorIcon /></div>
              <span className="burgerMenu__link-text">Мониторинг системы</span>
            </NavLink>
          ) : null}

          {visibleItems.map((item, idx) => (
            <NavLink
              key={`${item.href}-${idx}`}
              className="burgerMenu__link"
              to={item.href}
              onClick={onRequestClose}
            >
              <div className="burgerMenu__link-icon">{item.icon}</div>
              <span className="burgerMenu__link-text">{item.text}</span>
              {getNotificationCountForPath(item.href, counts) > 0 && (
                <b className="admin-notification-badge" title={"Непрочитанных уведомлений: " + getNotificationCountForPath(item.href, counts)}>
                  {formatNotificationCount(getNotificationCountForPath(item.href, counts))}
                </b>
              )}
            </NavLink>
          ))}

          {!visibleItems.length && user?.role !== 'superAdmin' ? (
            <div className="burgerMenu__emptyAccess">
              Нет доступных разделов. Попроси superAdmin выдать доступ.
            </div>
          ) : null}

          <button className="burgerMenu__logout" onClick={onRequestLogout}>
            Выйти с аккаунта
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RenderBurgerMenu;
